import axios from "axios";
import getCurrentActor from "libs/userInfo/getCurrentActor";

// Create a centralized axios instance for api3.2/canmail endpoints with base URL logic
const createCanmailApiInstance = () => {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000/api3.2/canmail'
    : '/api3.2/canmail';

  return axios.create({
    baseURL,
    withCredentials: true,
    // Set timeout for attachment operations (60 seconds)
    // Large attachments (base64 encoded) can take 10-30+ seconds to upload and process
    timeout: 60000, // 60 seconds
  });
};

// Get the canmail API instance
const canmailApi = createCanmailApiInstance();

// Create a centralized axios instance for api3.2/mail-list endpoints (list management)
const createMailListApiInstance = () => {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000/api3.2/mail-list'
    : '/api3.2/mail-list';

  return axios.create({
    baseURL,
    withCredentials: true,
    timeout: 30000,
  });
};

// Get the mail-list API instance
const mailListApi = createMailListApiInstance();

// Helper function to get X-Forwarded-Host header
const getForwardedHost = (apiUrl) => {
  // If apiUrl is missing or empty, use window.location.host
  if (!apiUrl) {
    return window.location.host;
  }
  
  // For relative URLs (starting with /), use window.location.host directly
  if (apiUrl.startsWith("/")) {
    return window.location.host;
  }
  
  // For absolute URLs, parse them to extract the host
  try {
    const parsedUrl = new URL(apiUrl);
    // Ensure we always return a valid host, fallback to window.location.host if empty
    return parsedUrl.host || window.location.host;
  } catch (error) {
    // If parsing fails, fallback to window.location.host
    return window.location.host;
  }
};

/**
 * Map backend error codes to user-friendly messages
 * @param {string} errorCode - Backend error code
 * @returns {string} User-friendly error message
 */
const getUserFriendlyError = (errorCode) => {
  const errorMap = {
    INVALID_REQUEST: "Please check your inputs and try again.",
    LIST_NOT_FOUND: "The selected mailing list is empty or doesn't exist. Please choose a different list.",
    CONTACT_NOT_FOUND: "Email address not found.",
    INVALID_SCHEDULE_DATE: "Please select a date at least 1 minute in the future.",
    ATTACHMENT_TOO_LARGE: "Attachments exceed 10MB limit. Please remove some files.",
    CAMPAIGN_ALREADY_PROCESSED: "This email has already been sent and cannot be modified.",
    DATABASE_ERROR: "A database error occurred. Please try again.",
    POSTAL_API_ERROR: "Email service error. Please try again later.",
    CAMPAIGN_NOT_FOUND: "Scheduled email not found.",
    ATTACHMENT_INDEX_OUT_OF_RANGE: "Invalid attachment index. Please refresh and try again.",
    ATTACHMENT_SIZE_EXCEEDED: "Total attachment size exceeds 10MB limit. Please remove some files.",
  };

  return errorMap[errorCode] || "An unexpected error occurred. Please try again.";
};

//=================================GET=============================================//
export const getMailLists = async () => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/GetMailLists`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};

export const getMailListsForContact = async (contactID) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/getMailListsForContact?contactID=${contactID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET getMailListsForContact :>> ", error);
  }
};

export const getMailListTypes = async () => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/GetMailListTypes`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};

export const getCompanyContacts = async (companyId, listID, listTypeID) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/getCompanyContacts?companyID=${companyId}&listID=${listID}&listTypeID=${listTypeID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
    return [];
  }
};

export const getListContacts = async (listID, listTypeID) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/getListContacts?listID=${listID}&listTypeID=${listTypeID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};

export const GetListConsentsFromContact = async (contactID) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/getListConsentsFromContact?contactID=${contactID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET getListConsentsFromContact :>> ", error);
  }
};

export const GetConsentStatus = async (contactID, listID, listTypeID) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/GetConsentStatus?contactID=${contactID}&listID=${listID}&listTypeID=${listTypeID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET GetConsentStatus :>> ", error);
  }
};

export const GetConsentStatusLog = async () => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/GetConsentStatusLog/`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET GetConsentStatusLog :>> ", error);
  }
};

export const GetContactFromEmail = async (email) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/GetContactFromEmail?email=${email}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET GetContactFromEmail :>> ", error);
  }
};

/**
 * Get all email templates
 * GET /api3.2/canmail/templates
 *
 * The backend paginates (default 100), so a limit is passed explicitly — the
 * Template Manager shows the whole library rather than a silently truncated slice.
 *
 * @param {{ limit?: number, offset?: number }} [options]
 */
export const GetEmailTemplates = async ({ limit = 500, offset = 0 } = {}) => {
  try {
    const { data } = await canmailApi.get('/templates', {
      params: { limit, offset },
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch templates");
    }

    const templates = data.data.templates || [];
    const totalCount = data.data.total_count;
    if (typeof totalCount === "number" && totalCount > templates.length + offset) {
      console.warn(
        `[CANmailAPI] Template list truncated: showing ${templates.length} of ${totalCount}. Raise the limit or paginate.`
      );
    }

    return templates;
  } catch (error) {
    console.error("ERROR GetEmailTemplates:", error);
    throw error;
  }
};

/**
 * Get single template details
 * GET /api3.2/canmail/templates/:templateId
 *
 * NEW ENDPOINT - No API 2.0 equivalent
 */
export const GetTemplateDetails = async (templateId) => {
  try {
    const { data } = await canmailApi.get(`/templates/${templateId}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch template");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR GetTemplateDetails:", error);
    throw error;
  }
};

export const GetEmailsFromList = async (listID) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/GetEmailsFromList?listID=${listID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });
    return data;
  } catch (error) {
    console.log("ERROR GetEmailsFromList DATA :>> ", error);
  }
};

//=================================Create & Update=================================//
export const CreateList = async (listName, listTypeID, listContactIDs) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/CreateMailList?listName=${listName}&listTypeID=${listTypeID}&listOfContacts=${listContactIDs}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};

export const CreateConsentStatus = async (consentStatusList) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/api2.0/CANmail/createConsentStatus`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
      data: consentStatusList,
    });

    return data;
  } catch (error) {
    console.log("ERROR CREATING CONSENT STATUS :>> ", error);
  }
};

export const CreateConsentStatusWithReason = async (contactID, listID, added_or_removed, reason) => {
  try {
    const payload = {
      contactID,
      listID,
      added_or_removed,
      reason,
    };

    const { data } = await axios({
      method: "POST",
      url: `/api2.0/CANmail/CreateConsentStatusWithReason/`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
      data: payload,
    });

    return data;
  } catch (error) {
    console.log("ERROR CreateConsentStatusWithReason :>> ", error);
    throw error;
  }
};

export const CreateListType = async (listTypeName) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/CreateMailListType?listTypeName=${listTypeName}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};

export const updateMailingList = async (
  listID,
  updatedListName,
  updatedListTypeID,
  toBeInsertedIDs,
  toBeDeletedIDs
) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/updateMailingList?listID=${listID}&updatedListName=${updatedListName}&updatedListTypeID=${updatedListTypeID}&toBeInsertedIDs=${toBeInsertedIDs}&toBeDeletedIDs=${toBeDeletedIDs}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};

/**
 * Send immediate email to a list
 * POST /api3.2/canmail/send
 *
 * ⚠️ ATTACHMENT WARNING:
 * Backend attachment functionality has NOT been fully tested.
 * Implement attachment UI with beta warning and extensive logging.
 * Test collaboratively with backend team before production use.
 *
 * @param {number} list_id - List ID (0 for individual email, >0 for mail list)
 * @param {string} from - Sender email
 * @param {string} reply_to - Reply-to email (optional, defaults to from)
 * @param {string} subject - Email subject
 * @param {string} email_contents - HTML email body
 * @param {Array} attachments - Array of {name, type, data} objects (⚠️ BETA)
 * @param {string} to - Recipient email for individual emails (required when list_id = 0)
 * @returns {Promise<Object>} { campaign_id, emails_attempted, emails_queued, message }
 */
export const SendCANmail = async (
  list_id,
  from,
  reply_to,
  subject,
  email_contents,
  attachments = null,
  to = null  // Optional recipient email for individual emails (list_id = 0)
) => {
  try {
    // Validate that 'to' field is provided for individual emails
    if (list_id === 0 && (!to || to.trim() === "")) {
      throw new Error("'to' field is required when list_id = 0 (individual email)");
    }

    const payload = {
      list_id,
      from,
      reply_to: reply_to && reply_to.trim() !== "" ? reply_to : from, // Default to from
      subject,
      email_contents,
    };

    // Add 'to' field for individual emails (list_id = 0)
    if (list_id === 0 && to) {
      payload.to = to;
    }

    if (attachments && attachments.length > 0) {
      // ⚠️ CRITICAL: Log attachment operations for debugging
      console.log('[SendCANmail] ⚠️ BETA FEATURE: Attaching files', {
        count: attachments.length,
        files: attachments.map(a => ({
          name: a.name,
          type: a.type,
          size: a.data?.length || 0
        })),
        totalSize: attachments.reduce((sum, a) => sum + (a.data?.length || 0), 0)
      });
      payload.attachments = attachments;
    }

    const { data } = await canmailApi.post("/send", payload, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    // Handle new response format
    if (!data.success) {
      throw new Error(data.error?.message || "Failed to send email");
    }

    return data.data; // Return unwrapped data
  } catch (error) {
    console.error("ERROR SendCANmail:", error);
    // Re-throw with user-friendly message
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Send an immediate email to an ad-hoc, non-persisted recipient list.
 * POST /api3.2/canmail/send  (list_id: 0 + recipients[])
 *
 * The recipients are emailed directly — no mail list is created or saved anywhere.
 * Recipients picked from the address book carry their name (for [[first_name]]
 * personalization); free-typed emails omit it. Each recipient is recorded as an
 * individual email-history row (no campaign/bulk row) — visible on the History
 * "Individual" tab.
 *
 * @param {Array<{email: string, first_name?: string, last_name?: string, company_name?: string, contact_id?: number}>} recipients
 * @param {string} from - Sender email address
 * @param {string} reply_to - Reply-to email address (defaults to `from` when blank)
 * @param {string} subject - Email subject
 * @param {string} email_contents - HTML email body
 * @param {Array} attachments - Optional array of {name, type, data} objects
 * @returns {Promise<Object>} { campaign_id, emails_attempted, emails_queued, message }
 */
export const SendAdHocCANmail = async (
  recipients,
  from,
  reply_to,
  subject,
  email_contents,
  attachments = null
) => {
  try {
    if (!Array.isArray(recipients) || recipients.length === 0) {
      throw new Error("At least one recipient is required for an ad-hoc send");
    }

    const payload = {
      list_id: 0,
      recipients: recipients.map((r) => ({
        email: r.email,
        ...(r.first_name ? { first_name: r.first_name } : {}),
        ...(r.last_name ? { last_name: r.last_name } : {}),
        ...(r.company_name ? { company_name: r.company_name } : {}),
        ...(r.contact_id ? { contact_id: r.contact_id } : {}),
      })),
      from,
      reply_to: reply_to && reply_to.trim() !== "" ? reply_to : from, // Default to from
      subject,
      email_contents,
    };

    if (attachments && attachments.length > 0) {
      payload.attachments = attachments;
    }

    const { data } = await canmailApi.post("/send", payload, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to send email");
    }

    return data.data; // Return unwrapped data
  } catch (error) {
    console.error("ERROR SendAdHocCANmail:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Send a lightweight notification email (no history tracking, no contact lookup, no variable substitution)
 * POST /api3.2/canmail/send-notification
 *
 * @param {string} to - Recipient email address
 * @param {string} from - Sender email address
 * @param {string} reply_to - Reply-to email address
 * @param {string} subject - Email subject
 * @param {string} email_contents - HTML email body
 * @param {Array} attachments - Optional array of {name, type, data} objects
 * @returns {Promise<Object>} { success, ... }
 */
export const SendNotification = async (to, from, reply_to, subject, email_contents, attachments = null) => {
  try {
    const payload = { to, from, reply_to, subject, email_contents };

    if (attachments && attachments.length > 0) {
      payload.attachments = attachments;
    }

    const { data } = await canmailApi.post("/send-notification", payload, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to send notification");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR SendNotification:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Schedule email for future delivery
 * POST /api3.2/canmail/schedule
 *
 * @param {number} list_id - List ID (must be > 0)
 * @param {string} from - Sender email
 * @param {string} reply_to - Reply-to email
 * @param {string} subject - Email subject
 * @param {string} email_contents - HTML email body
 * @param {string} schedule_date - Calgary server time string in format "YYYY-MM-DDTHH:mm:ss" (e.g., "2025-07-09T15:30:00")
 * Note: Frontend timezone utility converts user's local time to Calgary time before calling this function
 * @param {Array} attachments - Array of {name, type, data} objects
 * @param {string} content_text - Text editor HTML content (optional - for future editing)
 * @param {string} content_drop - Unlayer editor base64 JSON (optional - for future editing)
 * @returns {Promise<Object>} { schedule_id, schedule_date, message }
 */
export const ScheduleCANmail = async (
  list_id,
  from,
  reply_to,
  subject,
  email_contents,
  schedule_date,
  attachments = null,
  content_text = null,
  content_drop = null
) => {
  try {
    const payload = {
      list_id,
      from,
      reply_to: reply_to && reply_to.trim() !== "" ? reply_to : from,
      subject,
      email_contents,
      schedule_date, // Send as-is (ISO 8601 format from date picker)
    };

    if (attachments && attachments.length > 0) {
      payload.attachments = attachments;
    }

    // Add content fields if provided (for future editing capability)
    if (content_text) {
      payload.content_text = content_text;
    }
    if (content_drop) {
      payload.content_drop = content_drop;
    }

    const { data } = await canmailApi.post("/schedule", payload, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to schedule email");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR ScheduleCANmail:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Create email template
 * POST /api3.2/canmail/templates
 *
 * @param {Object} payload
 * @param {string} payload.template_name
 * @param {string} [payload.template_content_text] HTML from the text (ReactQuill) editor
 * @param {string} [payload.template_content_drop] Base64 Unlayer design JSON (visual editor)
 * @param {string} [payload.rendered_html]         Pre-rendered HTML used for previews
 * @param {'text'|'drop'} [payload.editor_type]
 * @param {string} [payload.description]
 * @param {string} [payload.folder]
 * @param {string} [payload.tags]
 */
export const CreateTemplate = async (payload) => {
  try {
    const actor = getCurrentActor();
    const { data } = await canmailApi.post('/templates', {
      ...payload,
      created_by: actor.user_id,
      created_by_name: actor.user_name,
    }, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to create template");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR CreateTemplate:", error);
    throw error;
  }
};

/**
 * Update email template
 * PUT /api3.2/canmail/templates/:templateId
 *
 * The current user is stamped automatically as UpdatedBy. A version-history
 * snapshot is written server-side only when the content (text/drop/rendered_html)
 * changes — metadata-only edits (rename, folder, description, tags) don't version.
 */
export const UpdateTemplate = async (templateId, updates) => {
  try {
    const actor = getCurrentActor();
    const { data } = await canmailApi.put(`/templates/${templateId}`, {
      ...updates,
      updated_by: actor.user_id,
      updated_by_name: actor.user_name,
    }, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to update template");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR UpdateTemplate:", error);
    throw error;
  }
};

/**
 * List a template's version history (metadata only — no content bodies)
 * GET /api3.2/canmail/templates/:templateId/versions
 */
export const GetTemplateVersions = async (templateId) => {
  try {
    const { data } = await canmailApi.get(`/templates/${templateId}/versions`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch template versions");
    }

    return data.data.versions || [];
  } catch (error) {
    console.error("ERROR GetTemplateVersions:", error);
    throw error;
  }
};

/**
 * Get a single version's full content (for preview / restore)
 * GET /api3.2/canmail/templates/:templateId/versions/:versionId
 */
export const GetTemplateVersionDetails = async (templateId, versionId) => {
  try {
    const { data } = await canmailApi.get(`/templates/${templateId}/versions/${versionId}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch template version");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR GetTemplateVersionDetails:", error);
    throw error;
  }
};

/**
 * Restore a previous version's body onto the template
 * POST /api3.2/canmail/templates/:templateId/versions/:versionId/restore
 *
 * @returns the refreshed template record
 */
export const RestoreTemplateVersion = async (templateId, versionId) => {
  try {
    const actor = getCurrentActor();
    const { data } = await canmailApi.post(`/templates/${templateId}/versions/${versionId}/restore`, {
      updated_by: actor.user_id,
      updated_by_name: actor.user_name,
    }, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to restore template version");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR RestoreTemplateVersion:", error);
    throw error;
  }
};

/**
 * Duplicate a template into a new one
 * POST /api3.2/canmail/templates/:templateId/duplicate
 *
 * @param {number|string} templateId
 * @param {string} [name] Optional name for the copy (defaults to "Copy of <name>")
 */
export const DuplicateTemplate = async (templateId, name) => {
  try {
    const actor = getCurrentActor();
    const { data } = await canmailApi.post(`/templates/${templateId}/duplicate`, {
      name,
      created_by: actor.user_id,
      created_by_name: actor.user_name,
    }, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to duplicate template");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR DuplicateTemplate:", error);
    throw error;
  }
};

/**
 * Get all scheduled emails
 * GET /api3.2/canmail/scheduled
 */
export const GetScheduledEmails = async (status = "Pending", limit = 100) => {
  try {
    const { data } = await canmailApi.get(`/scheduled?status=${status}&limit=${limit}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      console.warn("GetScheduledEmails failed:", data.error);
      return [];
    }

    return data.data.scheduled_emails || [];
  } catch (error) {
    console.error("ERROR GetScheduledEmails:", error);
    return [];
  }
};

/**
 * Get scheduled email details
 * GET /api3.2/canmail/scheduled/:campaignId
 */
export const GetScheduledEmailDetails = async (campaignId) => {
  try {
    const { data } = await canmailApi.get(`/scheduled/${campaignId}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch scheduled email");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR GetScheduledEmailDetails:", error);
    throw error;
  }
};

/**
 * Update scheduled email
 * PUT /api3.2/canmail/scheduled/:campaignId
 */
export const UpdateScheduledEmail = async (campaignId, updates) => {
  try {
    const { data } = await canmailApi.put(`/scheduled/${campaignId}`, updates, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to update scheduled email");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR UpdateScheduledEmail:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Cancel (delete) scheduled email
 * DELETE /api3.2/canmail/scheduled/:campaignId
 */
export const CancelScheduledEmail = async (campaignId) => {
  try {
    const { data } = await canmailApi.delete(`/scheduled/${campaignId}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to cancel scheduled email");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR CancelScheduledEmail:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

//=================================ATTACHMENT CRUD OPERATIONS=================================//

/**
 * List all attachments for a scheduled email
 * GET /api3.2/canmail/scheduled/:scheduleId/attachments
 */
export const ListScheduledEmailAttachments = async (scheduleId) => {
  try {
    const { data } = await canmailApi.get(`/scheduled/${scheduleId}/attachments`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to list attachments");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR ListScheduledEmailAttachments:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Get individual attachment with data
 * GET /api3.2/canmail/scheduled/:scheduleId/attachments/:index
 */
export const GetScheduledEmailAttachment = async (scheduleId, index) => {
  try {
    const { data } = await canmailApi.get(`/scheduled/${scheduleId}/attachments/${index}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to get attachment");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR GetScheduledEmailAttachment:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Add attachment(s) to scheduled email
 * POST /api3.2/canmail/scheduled/:scheduleId/attachments
 */
export const AddScheduledEmailAttachments = async (scheduleId, attachments) => {
  try {
    const payload = { attachments };

    const { data } = await canmailApi.post(`/scheduled/${scheduleId}/attachments`, payload, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to add attachments");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR AddScheduledEmailAttachments:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Delete individual attachment from scheduled email
 * DELETE /api3.2/canmail/scheduled/:scheduleId/attachments/:index
 */
export const DeleteScheduledEmailAttachment = async (scheduleId, index) => {
  try {
    const { data } = await canmailApi.delete(`/scheduled/${scheduleId}/attachments/${index}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to delete attachment");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR DeleteScheduledEmailAttachment:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

/**
 * Replace individual attachment in scheduled email
 * PUT /api3.2/canmail/scheduled/:scheduleId/attachments/:index
 */
export const ReplaceScheduledEmailAttachment = async (scheduleId, index, newAttachment) => {
  try {
    const { data } = await canmailApi.put(
      `/scheduled/${scheduleId}/attachments/${index}`,
      newAttachment,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
        },
      }
    );

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to replace attachment");
    }

    return data.data;
  } catch (error) {
    console.error("ERROR ReplaceScheduledEmailAttachment:", error);
    if (error.response?.data?.error) {
      throw new Error(getUserFriendlyError(error.response.data.error.code));
    }
    throw error;
  }
};

//=====================================DELETE======================================//
export const deleteMailLists = async (listIDs) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/api2.0/CANmail/deleteMailLists?listIDs=${listIDs}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });
    return data;
  } catch (error) {
    console.log("ERROR :>> ", error);
  }
};

/**
 * Delete email template
 * DELETE /api3.2/canmail/templates/:templateId
 */
export const deleteEmailTemplate = async (template_id) => {
  try {
    const { data } = await canmailApi.delete(`/templates/${template_id}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canmailApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to delete template");
    }

    return true;
  } catch (error) {
    console.error("ERROR deleteEmailTemplate:", error);
    throw error;
  }
};

export const UnsubscribeFromList = async (contactID, listID) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/api2.0/CANmail/UnsubscribeFromList/?contactID=${contactID}&listID=${listID}`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });

    return data;
  } catch (error) {
    console.log("ERROR UnsubscribeFromList :>> ", error);
    throw error;
  }
};

//=====================================LIST ROSTER======================================//

/**
 * Full contact roster for a list — name, both emails, both phones, notes, company.
 * Read-only despite the "export" path, which exists because the endpoint was built
 * to feed a CSV. It is the only one that returns phone numbers; the legacy
 * /api2.0 getListContacts selects name + email_work only.
 * GET /api3.2/mail-list/:listId/export
 * @param {number} listId
 * @param {string} [forwardedHost] Tenant host to resolve the list against. Pass
 *   it when the listId was read from another backend, so both land on the same
 *   database — on localhost the two derive different hosts. Defaults to this
 *   client's own host, which is correct for every deployed caller.
 * @returns {Promise<{ list_id: number, count: number, contacts: Array }>}
 */
export const GetListRoster = async (listId, forwardedHost) => {
  const { data } = await mailListApi.get(`/${listId}/export`, {
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "X-Forwarded-Host": forwardedHost || getForwardedHost(mailListApi.defaults.baseURL),
    },
  });

  if (!data.success) {
    throw new Error(data.error?.message || "Failed to load the list roster");
  }

  return data.data;
};

/**
 * The roster as it stood at the end of a given day, rebuilt from list_consent_log.
 * A list like "Quantum Healing Subscribers" holds only current membership —
 * cancelling deletes the row — so a past day's members can't be read from it.
 * Same row shape as GetListRoster.
 * GET /api3.2/mail-list/:listId/roster-as-of?date=YYYY-MM-DD
 * @param {number} listId
 * @param {string} date "YYYY-MM-DD"; the server resolves it to end of that day.
 * @param {string} [forwardedHost] Tenant host to resolve the list against — see
 *   GetListRoster.
 * @returns {Promise<{ list_id: number, as_of: string, count: number, contacts: Array }>}
 */
export const GetListRosterAsOf = async (listId, date, forwardedHost) => {
  const { data } = await mailListApi.get(`/${listId}/roster-as-of`, {
    params: { date },
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "X-Forwarded-Host": forwardedHost || getForwardedHost(mailListApi.defaults.baseURL),
    },
  });

  if (!data.success) {
    throw new Error(data.error?.message || "Failed to load the historical roster");
  }

  return data.data;
};

//=====================================LIST CLEANING (hard-bounce removal)======================================//

/**
 * Preview the members of a list whose email address has hard-bounced (permanently
 * undeliverable). Read-only — makes no changes.
 * GET /api3.2/mail-list/:listId/hard-failed
 * @param {number} listId
 * @returns {Promise<{ list_id: number, count: number, contacts: Array }>}
 */
export const GetHardFailedContacts = async (listId) => {
  const { data } = await mailListApi.get(`/${listId}/hard-failed`, {
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "X-Forwarded-Host": getForwardedHost(mailListApi.defaults.baseURL),
    },
  });

  if (!data.success) {
    throw new Error(data.error?.message || "Failed to load hard-bounced contacts");
  }

  return data.data;
};

/**
 * Remove all hard-bounced members from a list and log a consent revocation for each.
 * The set is recomputed server-side.
 * POST /api3.2/mail-list/:listId/clean
 * @param {number} listId
 * @returns {Promise<{ list_id: number, removed_count: number, removed: Array }>}
 */
export const CleanHardFailedContacts = async (listId) => {
  const { data } = await mailListApi.post(`/${listId}/clean`, {}, {
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "X-Forwarded-Host": getForwardedHost(mailListApi.defaults.baseURL),
    },
  });

  if (!data.success) {
    throw new Error(data.error?.message || "Failed to clean the list");
  }

  return data.data;
};

/**
 * Full contact roster for a list, shaped for CSV export. Read-only.
 * GET /api3.2/mail-list/:listId/export
 * @param {number} listId
 * @returns {Promise<{ list_id: number, count: number, contacts: Array }>}
 */
export const GetListContactsForExport = async (listId) => {
  const { data } = await mailListApi.get(`/${listId}/export`, {
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "X-Forwarded-Host": getForwardedHost(mailListApi.defaults.baseURL),
    },
  });

  if (!data.success) {
    throw new Error(data.error?.message || "Failed to export the mailing list");
  }

  return data.data;
};

//=====================================TEST AND OTHER======================================//
export const TestAPI = async () => {
  try {
    const { data } = await axios({
      method: "GET",
      //url: `/api2.0/CANmail/GetConsentStatus?emailAddress=john@streamdata.com&listID=10&listTypeId=1`,
      url: `/api2.0/CANmail/GetConsentStatus?emailAddress=john@streamdata.com&listID=10&listTypeId=1`,
      headers: { "Content-Type": "application/json;charset=UTF-8" },
    });
    return data;
  } catch (error) {
    console.log("ERROR GET CONTACT DATA :>> ", error);
  }
};
